export interface RunChunkedOptions {
  concurrency: number;
}

export interface RunChunkedResult<T> {
  /** Items whose Promise resolved. */
  succeeded: T[];
  /** Items whose Promise rejected, with the rejection reason. */
  failures: { item: T; reason: unknown }[];
}

/**
 * Run an async function over a list of items in fixed-size chunks. Caps
 * concurrent in-flight Promises to prevent HTTP/2 connection exhaustion
 * (~100 per origin) and 429 rate-limit responses. Within each chunk, items
 * run via Promise.allSettled so one failure doesn't reject the chunk.
 *
 * @param items - inputs to process
 * @param fn - per-item async function
 * @param options - { concurrency: chunk size; default 8 is conservative for HTTP/2 }
 * @returns succeeded items + failed items with rejection reasons
 */
export async function runChunked<T>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
  options: RunChunkedOptions = { concurrency: 8 }
): Promise<RunChunkedResult<T>> {
  const { concurrency } = options;
  const succeeded: T[] = [];
  const failures: { item: T; reason: unknown }[] = [];

  for (let i = 0; i < items.length; i += concurrency) {
    const chunk = items.slice(i, i + concurrency);
    const results = await Promise.allSettled(chunk.map(fn));
    results.forEach((result, j) => {
      if (result.status === 'fulfilled') {
        succeeded.push(chunk[j]);
      } else {
        failures.push({ item: chunk[j], reason: result.reason });
      }
    });
  }

  return { succeeded, failures };
}
